import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import clsx from 'clsx';

import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  CheckboxChecked,
  CheckboxOutlined,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import { TextWithIcon } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import TableDelete from '../../atoms/Icons/TableDelete';
import Edit from '../../atoms/Icons/Edit';
import Copy from '../../atoms/Icons/Copy';

const useStyles = makeStyles()(() => ({
  container: {
    position: 'relative',
    ...scrollBarStyles,
  },
  stickyHeader: {
    height: '40px',
  },
  headerCell: {
    fontSize: '12px',
    lineHeight: '12px',
    fontWeight: 500,
    color: patientAccessTheme.palette.text.mediumemphasis,
    padding: '14px 0px 14px 14px',
    border: '1px solid' + patientAccessTheme.palette.grey[300],
    backgroundColor: patientAccessTheme.palette.grey[400],
  },
  bodyCell: {
    fontSize: '12px',
    lineHeight: '160%',
    fontWeight: 400,
    color: patientAccessTheme.palette.text.mediumemphasis,
    padding: '14px 0px 14px 14px',
    border: '1px solid' + patientAccessTheme.palette.grey[300],
    background: patientAccessTheme.palette.status.white,
  },
  actionsCell: {
    padding: 0,
    border: '1px solid' + patientAccessTheme.palette.grey[300],
    background: patientAccessTheme.palette.background.default,
  },
  checkbox: {
    marginLeft: '6px',
  },
  stickyColumn: {
    position: 'sticky',
    left: 0,
    zIndex: 3,
    width: 50, // match to header
    minWidth: 50,
    maxWidth: 50,
  },

  stickyColumn2: {
    position: 'sticky',
    left: 50, // sum of previous sticky
    zIndex: 3,
    width: 50,
    minWidth: 50,
    maxWidth: 50,
  },

  stickyColumn3: {
    position: 'sticky',
    left: 100,
    zIndex: 3,
    width: 200, // match to first data column's width
    minWidth: 200,
    maxWidth: 200,
  },
  menuItem: {
    fontSize: '12px',
    lineHeight: '12px',
    fontWeight: 500,
    color: patientAccessTheme.palette.text.mediumemphasis,
    padding: '8px 16px',
  },
}));

interface Column {
  accessor: string;
  name: string;
  width?: number | string;
  cell?: (
     
    value: any,
     
    row?: any,
  ) => React.JSX.Element;
}

export interface ScenarioTableProps {
  data: any[];
  columns: Column[];
  height?: string;
   
  onSelectionChange?: (selectedRows: any[]) => void;
   
  handleMenuItemClick?: (menu: string, row: any) => void;
}

const ScenarioTable: React.FC<ScenarioTableProps> = ({
  data,
  columns,
  height,
  onSelectionChange,
  handleMenuItemClick,
}) => {
  const { classes } = useStyles();
  const menuActions = [
    {
      label: 'Edit',
      icon: <Edit />,
    },
    {
      label: 'Delete',
      icon: <TableDelete />,
    },
    {
      label: 'Duplicate',
      icon: <Copy />,
    },
  ];
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuRowIndex, setMenuRowIndex] = React.useState<number | null>(null);
  const [selectedRows, setSelectedRows] = React.useState<number[]>([]);

  const handleCheckboxChange = (rowIndex: number) => {
    const isSelected = selectedRows.includes(rowIndex);
    const newSelected = isSelected
      ? selectedRows.filter((i) => i !== rowIndex)
      : [...selectedRows, rowIndex];
    setSelectedRows(newSelected);
    onSelectionChange?.(newSelected.map((i) => data[i]));
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    index: number,
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuRowIndex(index);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuRowIndex(null);
  };

  return (
    <TableContainer
      component={Paper}
      className={classes.container}
      style={{ maxHeight: height }}
    >
      <Table stickyHeader>
        <TableHead className={classes.stickyHeader}>
          <TableRow>
            <TableCell
              colSpan={2}
              className={clsx(classes.headerCell, classes.stickyColumn)}
              style={{ textAlign: 'center', zIndex: 1000 }}
            >
              Actions
            </TableCell>
            {columns?.map((column, index) => (
              <TableCell
                key={column.accessor}
                className={
                  index === 0
                    ? `${classes.headerCell} ${classes.stickyColumn3}`
                    : classes.headerCell
                }
                style={{
                  minWidth: column.width,
                  zIndex: index === 0 ? 1000 : 3,
                }}
              >
                {column.name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.map((row, index) => (
            <TableRow key={index}>
              <TableCell
                className={clsx(classes.actionsCell, classes.stickyColumn)}
              >
                <Checkbox
                  color="primary"
                  className={classes.checkbox}
                  icon={<CheckboxOutlined width={'18px'} height={'18px'} />}
                  checkedIcon={
                    <CheckboxChecked width={'20px'} height={'20px'} />
                  }
                  onChange={() => handleCheckboxChange(index)}
                />
              </TableCell>
              <TableCell
                className={clsx(classes.actionsCell, classes.stickyColumn2)}
              >
                <IconButton
                  onClick={(e) => handleMenuClick(e, index)}
                  size="large"
                >
                  <MoreVertIcon
                    style={{
                      fill: patientAccessTheme.palette.grey[500],
                      width: '20px',
                      height: '20px',
                    }}
                  />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl) && menuRowIndex === index}
                  onClose={handleClose}
                >
                  {menuActions.map(({ label, icon }) => (
                    <MenuItem
                      key={label}
                      onClick={() => {
                        handleMenuItemClick &&
                          handleMenuItemClick(label, data[index]);
                        handleClose();
                      }}
                      className={classes.menuItem}
                    >
                      <TextWithIcon
                        label={label}
                        startIcon={icon}
                        startIconRightMargin="5px"
                      />
                    </MenuItem>
                  ))}
                </Menu>
              </TableCell>
              {columns?.map((column, colIndex) => (
                <TableCell
                  key={column.accessor}
                  className={
                    colIndex === 0
                      ? clsx(classes.bodyCell, classes.stickyColumn3)
                      : classes.bodyCell
                  }
                  style={{
                    background:
                      colIndex === 0
                        ? patientAccessTheme.palette.background.default
                        : patientAccessTheme.palette.status.white,
                  }}
                >
                  {column?.cell
                    ? column?.cell(row[column.accessor], row)
                    : row[column.accessor]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ScenarioTable;
